// 시간 입력·표시 — 사람이 치는 글자와 사람이 읽는 글자
//
// **이 층은 문법을 새로 만들지 않는다.** 무엇이 유효한 시간인가의 판단은 여전히
// src/core/time/brand.ts 하나가 내리고, 여기서 하는 일은 사람이 친 글자를 그 정규형으로
// 옮기고 정규형을 사람이 읽는 글자로 돌려놓는 것뿐이다. 문법의 주인이 둘이 되면 같은
// `1:00` 이 어디서 읽혔느냐에 따라 다른 수가 되는데, 그 차이는 화면에 보이지 않는다.
//
// 층을 하나 더 둔 이유는 정규형이 사람 입력을 그대로 받기에는 엄격하기 때문이다.
// brand.ts 는 분을 두 자리 이상으로 강제하고 앞의 `+` 를 받지 않는데, 판정 계약이
// 사람이 칠 글자로 적어 둔 값은 `-0:00.5` · `+0:00.1` · `1:00` · `0:40` 처럼 전부
// 한 자리 분이고 양수 부호가 붙는다. 정규형을 느슨하게 풀면 저장·전송 경계의 값이
// 여러 모양을 갖게 되므로, 느슨함을 입력 층에만 둔다.

import {
  type Duration,
  type Instant,
  formatMmss,
  parseMmss,
  toMillis,
} from '../time/brand.ts'

// 분 자릿수를 세지 않고 초의 범위도 보지 않는다. 그 판단은 아래에서 정규형으로 옮긴 뒤
// brand.ts 가 한다 — 여기서 한 번 더 보면 규칙이 두 곳에 살고 둘이 갈라진다.
const HUMAN_INPUT = /^([+-])?(\d+):(\d+)(?:\.(\d))?$/

/**
 * 사람이 친 시간 글자를 기간으로 읽는다.
 *
 * 받는 것: `1:00` · `01:00` · `+0:00.1` · `-0:00.5` · `+5:30.5` · 앞뒤 공백.
 * 거절하는 것: 소수점 두 자리, 60 이상의 초, 한 자리 초, 콜론 없는 수, 부호 중복.
 */
export function parseTimeInput(text: string): Duration {
  const matched = HUMAN_INPUT.exec(text.trim())
  if (matched === null) {
    throw new RangeError(
      `시간 형식이 아니다: ${JSON.stringify(text)} — mm:ss 또는 mm:ss.s (앞에 + 또는 -)`,
    )
  }
  const [, sign, minutes, seconds, tenth] = matched
  const canonical =
    (sign === '-' ? '-' : '') +
    `${(minutes ?? '').padStart(2, '0')}:${seconds ?? ''}` +
    (tenth === undefined ? '' : `.${tenth}`)
  return parseMmss(canonical)
}

/**
 * 기간을 사람이 읽는 `m:ss.s` 로 적는다. **분 자리에 앞의 0 을 채우지 않는다.**
 *
 * 판정 계약이 트리거를 `R 1:42.0` · `M 0:22.0` 으로 적었고 그것이 화면에 뜰 글자다.
 * 0.1초 격자를 벗어난 값은 반올림하지 않고 거절한다 — 조용히 반올림하면 그 자리가
 * 계산이 격자를 벗어났다는 사실을 감추는 자리가 된다.
 */
export function formatDuration(value: Duration): string {
  return formatMmss(value).replace(/^(-?)0(\d:)/, '$1$2')
}

/**
 * 착탄 간격을 적는다. **양수에도 부호를 붙인다.**
 *
 * 부호가 의도를 나타내는 유일한 표시라, 양수에서 생략하면 "간격을 아직 안 넣었다" 와
 * "0 보다 크게 넣었다" 가 같은 글자가 된다.
 */
export function formatSignedOffset(value: Duration): string {
  const body = formatDuration(value)
  return toMillis(value) < 0 ? body : `+${body}`
}

/** 앱 카운트다운. 빼기 기호는 하이픈이 아니라 U+2212 다 — 숫자 앞에서 폭이 맞는다. */
export function formatCountdown(value: Duration): string {
  return `T−${formatDuration(value)}`
}

const DAY_MILLIS = 86_400_000

/**
 * 착탄 절대 시각을 UTC `hh:mm:ss.s` 로 적는다. 이 제품은 UTC 전용이라 시간대 변환이 없다.
 *
 * 정본의 반례가 사람에게 보이는 글자가 이것이다 — 착탄축이면 `12:06:59.0`,
 * 개시축이면 `12:07:59.0`. 계산 원장 페이지가 두 줄을 나란히 찍는다.
 */
export function formatImpactClock(at: Instant): string {
  const millis = toMillis(at)
  if (millis % 100 !== 0) {
    throw new RangeError(`시각을 적을 수 없다 (${String(millis)}ms) — 이 칸은 0.1초 해상도다`)
  }
  const inDay = ((millis % DAY_MILLIS) + DAY_MILLIS) % DAY_MILLIS
  const tenth = (inDay / 100) % 10
  const totalSeconds = Math.floor(inDay / 1000)
  const hh = Math.floor(totalSeconds / 3600)
  const mm = Math.floor(totalSeconds / 60) % 60
  const ss = totalSeconds % 60
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}.${String(tenth)}`
}
